import { useTranslation } from 'react-i18next';
import { useState } from 'react';

interface PasswordFormProps {
  email: string;
  onEmailChange: (email: string) => void;
  onSubmit: (
    password: string,
    confirmPassword?: string,
    isForgotPassword?: boolean,
    referralCode?: string,
  ) => void;
  mode: 'sign_in' | 'sign_up';
  error?: string;
  loading?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  referralCode?: string;
  onReferralCodeChange?: (code: string) => void;
  referralCodeFromUrl?: boolean;
}

export const PasswordForm = ({
  email,
  onEmailChange,
  onSubmit,
  mode,
  error,
  loading,
  inputRef,
  referralCode,
  onReferralCodeChange,
  referralCodeFromUrl,
}: PasswordFormProps) => {
  const [t] = useTranslation('login');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = () => {
    if (mode === 'sign_up') {
      if (password !== confirmPassword) {
        setPasswordError(t('password_mismatch'));
        return;
      }
      if (password.length < 8) {
        setPasswordError(t('password_too_short'));
        return;
      }
    }
    setPasswordError('');
    onSubmit(
      password,
      mode === 'sign_up' ? confirmPassword : undefined,
      false, // isForgotPassword
      referralCode,
    );
  };

  return (
    <>
      <div className="my-8 flex w-full flex-col gap-4 px-6">
        <input
          type="email"
          ref={inputRef}
          className="input input-bordered"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          placeholder={t('placeholder_email')}
          autoComplete="email"
        />
        <label className="input input-bordered relative flex items-center">
          <input
            type={showPassword ? 'text' : 'password'}
            className="grow bg-transparent outline-none"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('placeholder_password')}
            autoComplete={
              mode === 'sign_up' ? 'new-password' : 'current-password'
            }
            aria-label={t('placeholder_password')}
          />
          <button
            type="button"
            className="btn btn-ghost btn-xs absolute right-2"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            onClick={() => setShowPassword((v) => !v)}
          >
            {showPassword ? '🙈' : '👁️'}
          </button>
        </label>
        {mode === 'sign_up' && (
          <label className="input input-bordered relative flex items-center">
            <input
              type={showConfirm ? 'text' : 'password'}
              className="grow bg-transparent outline-none"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('confirm_password')}
              autoComplete="new-password"
              aria-label={t('confirm_password')}
            />
            <button
              type="button"
              className="btn btn-ghost btn-xs absolute right-2"
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
              onClick={() => setShowConfirm((v) => !v)}
            >
              {showConfirm ? '🙈' : '👁️'}
            </button>
          </label>
        )}
        {mode === 'sign_up' && (
          <div className="relative">
            <input
              type="text"
              className="input input-bordered"
              value={referralCode || ''}
              onChange={(e) => onReferralCodeChange?.(e.target.value)}
              placeholder={t('referral_code')}
              disabled={referralCodeFromUrl} // Make it readonly if it comes from URL
            />
            {referralCodeFromUrl && (
              <div className="text-base-content/60 absolute right-3 top-1/2 -translate-y-1/2 text-xs">
                {t('from_link')}
              </div>
            )}
          </div>
        )}
        {(error || passwordError) && (
          <div className="text-center text-sm text-red-500">
            {error || passwordError}
          </div>
        )}
      </div>
      <div className="flex flex-col items-center gap-2">
        <button
          className="btn btn-primary mb-4 w-52 rounded-md"
          onClick={handleSubmit}
          disabled={loading}
        >
          {t(mode === 'sign_up' ? 'sign_up' : 'sign_in')}
        </button>
        {mode === 'sign_in' && (
          <button
            className="btn-link text-sm"
            onClick={() => onSubmit(password, undefined, true)}
          >
            {t('forgot_password')}
          </button>
        )}
      </div>
    </>
  );
};
